import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { SEO_NICHE_KEYWORDS } from "../../config/seoKeywords";
import { Link } from "react-router-dom";
import { useStateContext } from '../../components/contexts/ContextProvider';
import {
    MousePointerClick,
    UserX,
    Download,
    Mail,
    Smartphone,
    Zap,
    ArrowRight,
    ChevronDown,
} from "lucide-react";

const ACHETEUR_ETAPES = [
    {
        icon: MousePointerClick,
        title: "Achat en quelques clics",
        text: "Choisissez votre événement, votre formule et validez : un parcours court, pensé pour aller droit au but.",
    },
    {
        icon: UserX,
        title: "Sans création de compte obligatoire",
        text: "Sur le site web, vous pouvez finaliser l’achat et récupérer votre billet sans passer par une inscription préalable.",
    },
    {
        icon: Download,
        title: "Téléchargement immédiat",
        text: "Dès la confirmation, téléchargez votre ticket directement depuis le navigateur — en image ou en PDF, selon ce qui vous convient.",
    },
    {
        icon: Mail,
        title: "E-mail de confirmation",
        text: "Vous recevez votre billet et la confirmation de paiement dans votre boîte mail : gardez-les comme preuve et pour un accès hors connexion.",
    },
    {
        icon: Smartphone,
        title: "Retrouvez tout dans l’app",
        text: (
            <>
                En vous connectant plus tard à l’
                <Link to="/go" className="underline" target="_blank" rel="noopener noreferrer">
                    appli Events Billetterie
                </Link>
                , vous retrouvez vos billets à tout moment, au même endroit.
            </>
        ),
    },
];

export default function ExpressPurchase() {
    const imagePath = process.env.REACT_APP_IMAGE_PATH;
    const [acheteurOpen, setAcheteurOpen] = useState(0);
    const { user } = useStateContext();
    const canonicalUrl = "https://eventsbilletterie.com/achat-express";
    const seoTitle = "Achat express billet evenement sans compte | Events billetterie";
    const seoDescription =
        "Achat express de billets evenementiels sans creation de compte : paiement rapide, billet immediat PDF ou image, confirmation e-mail et reprise dans l'application Events.";
    const seoKeywords =
        `achat express billet, acheter ticket sans compte, ticket pdf, ${SEO_NICHE_KEYWORDS}`;
    const seoImage = "https://eventsbilletterie.com/assets/images/hero-float-paiement.png";
    const expressPurchaseSeoJsonLd = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: seoTitle,
        description: seoDescription,
        url: canonicalUrl,
        inLanguage: "fr-CI",
        about: [
            "Achat express de billets",
            "Billet sans compte",
            "Paiement en ligne evenement",
            "Billet PDF et confirmation e-mail",
        ],
    };
    const toggleAcheteur = (index) => {
        setAcheteurOpen((prev) => (prev === index ? null : index));
    };

    return (
        <main className="nc-PageAbout relative overflow-hidden">
            <Helmet>
                <html lang="fr" />
                <title>{seoTitle}</title>
                <meta name="description" content={seoDescription} />
                <meta name="keywords" content={seoKeywords} />
                <meta
                    name="robots"
                    content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
                />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="fr_CI" />
                <meta property="og:site_name" content="Events Billetterie" />
                <meta property="og:title" content={seoTitle} />
                <meta property="og:description" content={seoDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content={seoImage} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seoTitle} />
                <meta name="twitter:description" content={seoDescription} />
                <meta name="twitter:image" content={seoImage} />
                <meta name="twitter:site" content="@events_billetterie" />
                <link rel="canonical" href={canonicalUrl} />
                <script type="application/ld+json">
                    {JSON.stringify(expressPurchaseSeoJsonLd)}
                </script>
            </Helmet>

            <div className="nc-BgGlassmorphism pointer-events-none absolute inset-x-0 top-0 min-h-[22rem] pl-20 py-24 flex overflow-hidden z-0">
                <span className="block bg-primary-6000/15 w-72 h-72 rounded-full mix-blend-multiply filter blur-3xl opacity-40 lg:w-96 lg:h-96" />
                <span className="block bg-[#04868b]/15 w-72 h-72 -ml-20 mt-24 rounded-full mix-blend-multiply filter blur-3xl opacity-35 lg:w-96 lg:h-96" />
            </div>

            <div className="container relative z-[1] px-4 pt-12 pb-20 md:pt-16 md:pb-28">
                <div className="max-w-3xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 rounded-full bg-primary-6000/10 text-primary-700 dark:text-primary-400 px-4 py-1.5 text-sm font-medium mb-6">
                        <Zap className="w-4 h-4" aria-hidden />
                        Achat express
                    </div>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 dark:text-neutral-50 tracking-tight leading-tight">
                        <span className="block">
                            Nous avons pensé à vous !
                        </span>
                    </h1>
                    <p className="mt-6 text-lg md:text-xl text-neutral-600 dark:text-neutral-400 leading-relaxed">
                        Parfois vous ne pouvez pas (ou ne voulez pas) passer par l’application&nbsp;: mémoire du téléphone
                        saturée, appareil trop modeste, ou simple urgence pour réserver. Sur le site officiel{" "}, vous
                        achetez en 10 secondes minimum, sans installation&nbsp;,&nbsp;sans obligation de créer un compte.
                    </p>
                </div>

                <div className="mt-12 max-w-4xl mx-auto px-2 sm:px-4">
                    {/* <p className="text-center text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-6">
                        Paiement sécurisé puis confirmation — le parcours express sur le web
                    </p> */}
                    <div className="flex flex-col sm:flex-row sm:items-stretch sm:justify-between gap-6 sm:gap-8">
                        <figure className="flex flex-col items-center sm:items-start flex-1 min-w-0">
                            <img
                                className="w-full max-w-[min(100%,280px)] sm:max-w-none object-contain rounded-xl shadow-[0_12px_40px_-8px_rgba(0,0,0,0.18)] ring-1 ring-black/[0.06]"
                                alt="Aperçu du parcours de paiement sécurisé"
                                loading="lazy"
                                decoding="async"
                                src={`${imagePath}images/hero-float-paiement.png`}
                            />
                            <figcaption className="mt-3 text-center sm:text-left text-xs font-medium text-neutral-500 dark:text-neutral-400">
                                Paiement
                            </figcaption>
                        </figure>
                        <figure className="flex flex-col items-center sm:items-end flex-1 min-w-0">
                            <img
                                className="w-full max-w-[min(100%,280px)] sm:max-w-none ml-auto object-contain rounded-xl shadow-[0_12px_40px_-8px_rgba(0,0,0,0.18)] ring-1 ring-black/[0.06]"
                                alt="Aperçu de la confirmation et du billet"
                                loading="lazy"
                                decoding="async"
                                src={`${imagePath}images/hero-float-confirmation.png`}
                            />
                            <figcaption className="mt-3 text-center sm:text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 w-full">
                                Confirmation &amp; billet
                            </figcaption>
                        </figure>
                    </div>
                </div>

                {/* Acheteurs */}
                <section className="mt-20 md:mt-28 max-w-3xl mx-auto" aria-labelledby="heading-acheteurs-express">

                    <div className="mt-10 space-y-3">
                        {ACHETEUR_ETAPES.map(({ icon: Icon, title, text }, index) => {
                            const isOpen = acheteurOpen === index;
                            const panelId = `express-acheteur-panel-${index}`;
                            const headerId = `express-acheteur-header-${index}`;
                            return (
                                <div
                                    key={title}
                                    className="rounded-2xl border border-neutral-200/90 dark:border-neutral-700 bg-white/85 dark:bg-neutral-900/50 shadow-sm overflow-hidden"
                                >
                                    <h3 className="text-base m-0">
                                        <button
                                            type="button"
                                            id={headerId}
                                            className="flex w-full items-center gap-4 p-4 text-left hover:bg-neutral-50/80 dark:hover:bg-neutral-800/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-6000 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900"
                                            onClick={() => toggleAcheteur(index)}
                                            aria-expanded={isOpen}
                                            aria-controls={panelId}
                                        >
                                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-800/10 text-gray-800">
                                                <Icon className="w-5 h-5" strokeWidth={2} aria-hidden />
                                            </span>
                                            <span className="flex-1 font-semibold text-neutral-900 dark:text-neutral-100 pr-2">
                                                {title}
                                            </span>
                                            <ChevronDown
                                                className={`h-5 w-5 shrink-0 text-neutral-500 transition-transform duration-200 ${
                                                    isOpen ? "rotate-180" : ""
                                                }`}
                                                aria-hidden
                                            />
                                        </button>
                                    </h3>
                                    {isOpen && (
                                        <div
                                            id={panelId}
                                            role="region"
                                            aria-labelledby={headerId}
                                            className="border-t border-neutral-200/80 dark:border-neutral-700"
                                        >
                                            <p className="px-4 pb-4 pt-3 pl-[4.5rem] text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                                                {text}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link
                            to="/events"
                            className="inline-flex items-center gap-2 rounded-lg bg-primary-6000 hover:bg-primary-700 text-white font-semibold px-6 py-3 text-sm transition-colors"
                        >
                            Voir les événements
                            <ArrowRight className="w-4 h-4" aria-hidden />
                        </Link>
                        <Link
                            to="/application-mobile"
                            className="inline-flex items-center gap-2 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-primary-6000 underline-offset-4 hover:underline"
                        >
                            Découvrir l’application mobile
                            <ArrowRight className="w-4 h-4" aria-hidden />
                        </Link>
                    </div>
                </section>

                {/* Organisateurs */}
                <section className="mt-20 md:mt-28 max-w-6xl mx-auto" aria-labelledby="heading-org-express">
                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-center">
                        <div className="relative mx-auto w-full max-w-[560px] lg:max-w-none rounded-3xl bg-white/90 dark:bg-neutral-900/50 p-3 sm:p-4 shadow-sm">
                            <img
                                className="w-3/5 mx-auto h-auto rounded-2xl object-cover"
                                alt="Aperçu de la page événement et de la sélection des billets"
                                loading="lazy"
                                decoding="async"
                                src={`${imagePath}images/express-purchase-organizer-preview.png`}
                            />
                      

                            <img
                                className="absolute w-[60%] sm:w-[44%] md:w-[40%] lg:w-[60%] right-2 sm:right-4 bottom-2 sm:bottom-4 rounded-xl object-cover shadow-xl ring-1 ring-black/10"
                                alt="Aperçu du menu d'actions du lien de billetterie"
                                loading="lazy"
                                decoding="async"
                                src={`${imagePath}images/express-purchase-organizer-menu-preview.png`}
                            />
                        </div>

                        <div>
                            <h2
                                id="heading-org-express"
                                className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-neutral-50"
                            >
                                Un lien qui convertit mieux
                            </h2>
                            <p className="mt-3 text-neutral-600 dark:text-neutral-400">
                                Vous partagez un accès direct vers votre billetterie Events, vos participants arrivent
                                exactement où il faut, et l'achat se fait plus vite. Le résultat : un parcours plus fluide et
                                une expérience plus claire pour vendre davantage.
                            </p>

                            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                {user ? (
                                    <Link
                                        to="/dashboard/events/registration"
                                        className="inline-flex items-center gap-2 rounded-lg text-white bg-gray-800 font-semibold px-6 py-3 text-sm hover:bg-gray-800/80 transition-colors"
                                    >
                                        Ouvrir ma billetterie
                                        <ArrowRight className="w-4 h-4" aria-hidden />
                                    </Link>
                                ) : (
                                    <Link
                                        to="/create-account"
                                        className="inline-flex items-center gap-2 rounded-lg text-white bg-gray-800 font-semibold px-6 py-3 text-sm hover:bg-gray-800/80 transition-colors"
                                    >
                                        Ouvrir ma billetterie
                                        <ArrowRight className="w-4 h-4" aria-hidden />
                                    </Link>
                                )}
                                
                                <Link
                                    to="/offre-billetterie"
                                    className="text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-primary-6000 underline-offset-4 hover:underline"
                                >
                                    L'offre billetterie complète
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </main>
    );
}
